import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import forgotPasswordSchemas from "@rentiohq/shared-frontend/dist/forms/forgotPassword";
import { EField } from "@rentiohq/shared-frontend/dist/forms/forgotPassword/schema.forgotPassword.types";
import logger from "@rentiohq/shared-frontend/dist/logger";
import { createForm } from "@rentiohq/shared-frontend/dist/redux/form/form.actions";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as authApi from "@rentiohq/shared-frontend/dist/reduxV2/auth/auth.api";
import { ECaptchaVersion } from "@rentiohq/shared-frontend/dist/types/auth.types";
import { isValidEmail } from "@rentiohq/shared-frontend/dist/utils/email.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { isValidPhone } from "@rentiohq/shared-frontend/dist/utils/phone/phone.utils";
import { htmlTextAsReactText } from "@rentiohq/shared-frontend/dist/utils/text/text.utils";
import React from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { getReferrer } from "utils/navigation.utils";
import FormMultistep from "../../../../components/FormMultistep";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import * as S from "./ForgotPassword.styles";

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [formId] = React.useState(generateFormId());
  const [isSendingRestLink, setIsSendingRestLink] = React.useState(false);
  const [phoneOrEmailToReset, setPhoneOrEmailToReset] = React.useState<
    string | undefined
  >(undefined);

  React.useEffect(() => {
    dispatch(
      createForm({
        formId,
        schemas: forgotPasswordSchemas(),
        withSummary: false,
      }),
    );
  }, []);

  // Event handlers
  const handleCustomSubmitAction = async (formData: any) => {
    const phoneOrEmail = formData[EField.PhoneOrEmail];
    const captcha = formData[EField.Captcha];

    setIsSendingRestLink(true);

    try {
      let phone;
      let email;

      if (isValidPhone(phoneOrEmail)) {
        phone = phoneOrEmail;
      } else if (isValidEmail(phoneOrEmail)) {
        email = phoneOrEmail;
      }

      await authApi.requestPasswordReset({
        phone,
        email,
        captcha,
        captchaVersion: ECaptchaVersion.Legacy,
      });

      setPhoneOrEmailToReset(phoneOrEmail);
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });
    }

    setIsSendingRestLink(false);
  };

  const handlePressSignIn = () => {
    navigate("/auth/signin", {
      state: {
        referrer: getReferrer(location),
      },
    });
  };

  // Render
  const renderSuccess = (phoneOrEmail: string) => {
    return (
      <>
        <S.SuccessText>
          {htmlTextAsReactText(
            getLocalizedText("auth.request_password_reset.sent.message_2", {
              phoneOrEmail,
            }),
          )}
        </S.SuccessText>
        {renderExtraTrailingControls()}
      </>
    );
  };

  const renderForm = () => {
    return (
      <>
        <FormMultistep
          formId={formId}
          customSubmitAction={handleCustomSubmitAction}
          renderExtraTrailingControls={renderExtraTrailingControls}
          submitTitle={getLocalizedText(
            "auth.request_password_reset.submit.label",
          )}
        />
        {isSendingRestLink && <FullScreenActivityIndicator />}
      </>
    );
  };

  const renderExtraTrailingControls = () => (
    <>
      <Spacer weight={ESpacerWeight.W16} />
      <S.ButtonInfo>
        <span>
          {getLocalizedText("auth.request_password_reset.login.title")}{" "}
        </span>
        <ButtonBasic
          title={getLocalizedText("auth.request_password_reset.login.cta")}
          onPress={handlePressSignIn}
        />
      </S.ButtonInfo>
    </>
  );

  return (
    <Page>
      <InfoHeader
        title={getLocalizedText("auth.request_password_reset.title")}
        body={
          phoneOrEmailToReset
            ? undefined
            : getLocalizedText("auth.request_password_reset.body")
        }
      />
      <S.FormWrap>
        {phoneOrEmailToReset
          ? renderSuccess(phoneOrEmailToReset)
          : renderForm()}
      </S.FormWrap>
    </Page>
  );
};
